import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { AuthenticationService, Configuration, deepCopy } from '@sla-ng/core';
import { BehaviorSubject, catchError, firstValueFrom, from, map, Observable, of } from 'rxjs';
import { MenuItem } from '../../models/menu-item.model';
import { QueryParam } from '../../models/query-param.model';
import { GlobalConfigService } from '../../services/global-config.service';

@Component({
    selector: 'mat-sla-sidenav',
    templateUrl: './sla-sidenav.component.html',
    styleUrls: [ './sla-sidenav.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('subItem', [
            state('hiddenAnimated', style({
                height: '0px',
                overflow: 'hidden',
            })),
            state('visibleAnimated', style({
                height: '*',
                overflow: 'visible',
            })),
            transition('visibleAnimated <=> hiddenAnimated', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
        ]),
        trigger('subItemOpenClose', [
            state('close', style({
                transform: 'rotate(0deg)',
            })),
            state('open', style({
                transform: 'rotate(180deg)',
            })),
            transition('open <=> close', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)')),
        ]),
    ],
})
export class SlaSidenavComponent implements OnInit, OnChanges{
    @ViewChild('sidenav', { static: true })
    public sidenav: MatSidenav;

    @Input()
    public mode: 'over' | 'push' | 'side' = 'side';

    @Input()
    public workplaceConfigNames: string[];

    @Input()
    public menuItems: MenuItem[];

    @Input()
    public closeOnNavigation: boolean;

    @Input()
    public openMenuOnLogin: boolean;

    @Input()
    public showThemeMode: boolean = false;

    @Input()
    public useSharedMenuConfig: boolean = true;

    @Input()
    public set isLoggedIn(loggedIn: boolean) {
        this.loggedIn = loggedIn;
        void this.setMenuItems();
        loggedIn && this.openMenuOnLogin ? this.open() : this.close();
    }

    // eslint-disable-next-line @typescript-eslint/member-ordering
    @Output()
    public menuItemClicked: EventEmitter<MenuItem> = new EventEmitter<MenuItem>();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public loggedIn: boolean;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public activeLink: number;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public isDarkMode: boolean;
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public sharedMenuItems: MenuItem[] = [];

    public menuItemsObs$: Observable<MenuItem[]>;

    protected readonly configuration: Configuration;

    private menuItemsSubject: BehaviorSubject<MenuItem[]> = new BehaviorSubject([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor(
        private myGlobalConfigService: GlobalConfigService,
        private myChangeDetectorRef: ChangeDetectorRef,
        private myAuthenticationService: AuthenticationService,
        @Optional() configuration: Configuration
    ) {
        if (configuration != null && Object.keys(configuration).length > 0) {
            this.configuration = configuration;
        } else {
            this.configuration = new Configuration();
        }
    }

    public ngOnInit(): void {
        this.isDarkMode = localStorage.getItem('theme') === 'Dark';
        this.menuItemsObs$ = this.menuItemsSubject.asObservable();

        this.setDarkModeToBody();
        void this.setMenuItems();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes[ 'useSharedMenuConfig' ] || changes[ 'menuItems' ] || changes[ 'workplaceConfigNames' ]) {
            void this.setMenuItems();
        }
    }

    public toggle(): void {
        void this.sidenav.toggle();
        this.myChangeDetectorRef.markForCheck();
    }

    public open(): void {
        void this.sidenav.open();
        this.myChangeDetectorRef.markForCheck();
    }

    public close(): void {
        if (this.closeOnNavigation) {
            void this.sidenav.close();
            this.myChangeDetectorRef.markForCheck();
        }
    }

    public toggleMenuItem(index: number, close: boolean): void {
        this.activeLink = this.activeLink === index ? null : index;
        if (close && this.closeOnNavigation) {
            this.close();
        }
    }

    public storeThemeSelection(): void {
        localStorage.setItem('theme', this.isDarkMode ? 'Dark' : 'Light');
        this.setDarkModeToBody();
    }

    public prepareURLWithQueryParams(menuItem: MenuItem, userContextParams: Record<string, string>): MenuItem {
        if (menuItem.routerLink?.length && !menuItem.routerLink.includes('http') || menuItem.routerLink.startsWith(location.origin)){
            menuItem.routerLink = `${location.origin}${menuItem.routerLink?.replace(location.origin, '')}`;
        }
        menuItem.queryParams = { ...(menuItem.queryParams || {}), ...userContextParams };
        return menuItem;
    }
    public getParamsUrl(url: string, queryParams: QueryParam | Record<string, string> = {}): string {
        const queryParamsStr = new URLSearchParams(queryParams as Record<string, string>);
        if (queryParamsStr?.size) {
            return `${url}?${queryParamsStr}`;
        }
        return url;
    }
    private setDarkModeToBody(): void {
        const body: HTMLBodyElement = document.getElementsByTagName('body')[ 0 ];
        if (this.isDarkMode) {
            body.classList.add('mat-app-background', 'dark-theme-mode');
        } else {
            body.classList.remove('mat-app-background', 'dark-theme-mode');
        }
    }

    private async setMenuItems(): Promise<void> {
        const sharedMenuItems: MenuItem[] = this.useSharedMenuConfig ? this.myGlobalConfigService.getSharedMenuItems() : [];
        if (sharedMenuItems?.length) {
            const userContextParams: Record<string, string> = await firstValueFrom(this.prepareUserContextParams());
            const items: MenuItem[] = this.mapMenuItem(sharedMenuItems, userContextParams);
            this.menuItemsSubject.next(deepCopy(items));
        } else {
            // Fallback if no shared items
            this.menuItemsSubject.next(this.menuItems);
        }

    }

    private mapMenuItem(menuItems: MenuItem[], userContextParams: Record<string, string>): MenuItem[] {
        return menuItems.map((menuItem: MenuItem) => {
            if (menuItem.subItems) {
                return {
                    ...menuItem,
                    subItems: this.mapMenuItem(menuItem.subItems, userContextParams),
                };
            } else {
                return this.prepareURLWithQueryParams(menuItem, userContextParams);
            }
        });
    }
    private prepareUserContextParams(): Observable<Record<string, string>> {
        const workplaceName: string | Promise<string> = this.configuration.getValue('workplaceName');
        return (workplaceName instanceof Promise ? from(workplaceName) : of(workplaceName)).pipe(
            map(currentWorkplaceName => {
                const loginToken: string = this.myAuthenticationService?.[ 'token' ] || '';
                return {
                    loginToken,
                    workplace: currentWorkplaceName
                };
            }),
            catchError(err => {
                console.error('Error fetching workplaceName:', err);
                return of({});
            })
        );
    }
}
